/**
 * Вектор направления въезда для камер с распознаванием гос. номеров.
 * Внешний API хранит его в процентах от кадра (0–100): (x1,y1) → (x2,y2).
 */
export interface DirectionVector {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function isLicensePlateModel(modelName: string): boolean {
  const key = (modelName || '').trim().toLowerCase();
  return key.includes('license_plate') || key === 'lpd';
}

export function cameraHasLicensePlateModel(models: string[] | undefined | null): boolean {
  return (models ?? []).some(isLicensePlateModel);
}

/** Модель распознавания номеров среди привязанных к камере (первая найденная). */
export function findLicensePlateModel(models: string[] | undefined | null): string | null {
  return (models ?? []).find(isLicensePlateModel) ?? null;
}

/**
 * Задана ли у камеры зона ROI для распознавания номеров.
 *
 * null — по статусу внешнего API этого не понять (камера не в сети или статус ещё не пришёл):
 * в этом случае камеру не блокируем, чтобы не показать ложную плашку.
 */
export function hasLicensePlateRoi(
  status: { roi?: Record<string, any> | null; models?: unknown } | null | undefined,
  models: string[] | undefined | null
): boolean | null {
  const lprModel = findLicensePlateModel(models);
  if (!lprModel) return true;
  // Пустой статус — камера не в сети или ещё грузится
  if (!status || typeof status !== 'object' || Object.keys(status).length === 0) return null;

  const roi = status.roi && typeof status.roi === 'object' ? status.roi : {};
  return (models ?? []).filter(isLicensePlateModel).some((model) => {
    const preCrop = roi[model]?.pre_crop;
    return (
      !!preCrop &&
      Number.isFinite(Number(preCrop.w)) &&
      Number.isFinite(Number(preCrop.h)) &&
      Number(preCrop.w) > 0 &&
      Number(preCrop.h) > 0
    );
  });
}

function toFiniteNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * Разбирает ответ внешнего API и приводит вектор к процентам от кадра (0–100).
 * Формат хранения на стороне внешнего API не зафиксирован, поэтому определяем по значениям:
 *  - все значения в [0, 1]   → доли кадра;
 *  - все значения в [0, 100] → проценты;
 *  - иначе                   → пиксели исходного кадра (frameWidth × frameHeight).
 * Вырожденный (нулевой) вектор считаем отсутствующим.
 */
export function parseDirectionVector(
  raw: unknown,
  frameWidth?: number,
  frameHeight?: number
): DirectionVector | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  // Внешний API может отдать {vector: {...}} или сразу {x1, y1, x2, y2}
  const src = (o.vector && typeof o.vector === 'object' ? (o.vector as Record<string, unknown>) : o);
  const x1 = toFiniteNumber(src.x1);
  const y1 = toFiniteNumber(src.y1);
  const x2 = toFiniteNumber(src.x2);
  const y2 = toFiniteNumber(src.y2);
  if (x1 === null || y1 === null || x2 === null || y2 === null) return null;
  if (x1 === x2 && y1 === y2) return null;

  const values = [x1, y1, x2, y2];
  const maxAbs = Math.max(...values.map(Math.abs));

  if (maxAbs <= 1) {
    return { x1: x1 * 100, y1: y1 * 100, x2: x2 * 100, y2: y2 * 100 };
  }
  if (maxAbs <= 100) {
    return { x1, y1, x2, y2 };
  }

  const fw = frameWidth && frameWidth > 0 ? frameWidth : 1920;
  const fh = frameHeight && frameHeight > 0 ? frameHeight : 1080;
  return {
    x1: (x1 / fw) * 100,
    y1: (y1 / fh) * 100,
    x2: (x2 / fw) * 100,
    y2: (y2 / fh) * 100,
  };
}

/** Цвет вектора на оверлеях и в селекторе. */
export const DIRECTION_VECTOR_COLOR = '#3b82f6';

/**
 * Рисует стрелку (x1,y1) → (x2,y2) в координатах canvas.
 * Используется и на основном превью, и в модалке выбора.
 */
export function drawDirectionArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  options: { color?: string; lineWidth?: number; label?: string } = {}
): void {
  const color = options.color ?? DIRECTION_VECTOR_COLOR;
  const lineWidth = options.lineWidth ?? 3;
  const headLength = Math.max(10, lineWidth * 4);
  const angle = Math.atan2(y2 - y1, x2 - x1);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Наконечник стрелки
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - headLength * Math.cos(angle - Math.PI / 6), y2 - headLength * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - headLength * Math.cos(angle + Math.PI / 6), y2 - headLength * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();

  // Точка начала
  ctx.beginPath();
  ctx.arc(x1, y1, lineWidth + 2, 0, Math.PI * 2);
  ctx.fill();

  // Подпись — у наконечника, под стрелкой
  if (options.label) {
    ctx.font = 'bold 14px Arial';
    ctx.textBaseline = 'top';
    const textWidth = ctx.measureText(options.label).width;
    const padding = 6;
    let tx = x2 - textWidth / 2;
    let ty = y2 + headLength / 2 + padding;
    // Не выходим за границы canvas
    tx = Math.max(padding, Math.min(ctx.canvas.width - textWidth - padding, tx));
    if (ty + 16 > ctx.canvas.height) {
      ty = y2 - headLength - padding - 16;
    }
    ctx.fillText(options.label, tx, ty);
  }

  ctx.restore();
}
